import DocumentTitle from 'react-document-title';

import { Box } from '@hivebrite/design-system/Box';
import { Container } from '@hivebrite/design-system/Container';

import { GROUP_FEED_TYPE } from 'hb-react/frontoffice/apps/LiveFeed/constants/FeedType';
import useIsEnabledMyGroupsFeed from 'hb-react/frontoffice/apps/LiveFeed/hooks/useIsEnabledMyGroupsFeed';
import LiveFeedApp from 'hb-react/frontoffice/apps/LiveFeed/startup/LiveFeedAppClient';
import { getAmplitudeConfig } from 'hb-react/frontoffice/apps/LiveFeed/utils/getAmplitudeConfig';
import getFeedConfig from 'hb-react/frontoffice/apps/LiveFeed/utils/getFeedConfig';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import Login from 'hb-react/shared/apps/Topics/components/Login';
import MemberRequestCard from 'hb-react/shared/apps/Topics/components/MemberRequestCard';
import Grid, { Row, Col } from 'hb-react/shared/components/DesignSystem/Grid';
import { usePageMenuName, PAGES } from 'hb-react/shared/hooks/usePageMenuName';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

import TopicSideBar from '../TopicSideBar';

const TopicLiveFeedContainer = ({ topic, ...otherProps }) => {
  const { currentNetwork, currentUser } = useDefaultAppContexts();

  const topicsMenuItem = usePageMenuName(PAGES.FEED, s__('Topics|Feed'));
  const isEnabledMyGroupsFeed = useIsEnabledMyGroupsFeed();

  const { full_access: fullAccess } = topic;
  const liveFeedTabEnabled = topic.config?.live_feed_tab_enabled;
  const liveFeedAmplitudeConfig = getAmplitudeConfig({
    liveFeedType: GROUP_FEED_TYPE,
    myGroupsFeedIsEnabled: isEnabledMyGroupsFeed,
  });

  /*
   * Only apply for any connected user.
   * Group admin can disable the Group Live Feed.
   */
  if (currentUser && !liveFeedTabEnabled) {
    return null;
  }

  // On the PO user will always see the Login button
  // On the FO, if the user does not have full access (group is private and user is not part of it), user will see the Request to join button
  if (!currentUser || fullAccess === false) {
    return (
      <>
        <DocumentTitle
          title={generatePageTitle({
            title: topicsMenuItem,
            networkName: getPageTitleFromNetwork(currentNetwork),
            topicName: topic?.name,
          })}
        />
        <Container mt={5}>
          <Grid width="100%" mx={{ _: 0, m: 6, l: 8 }}>
            <Row>
              <Col
                offset={({ numCols }) => ({
                  _: 0,
                  m: 1 / numCols,
                  l: 0,
                  xl: 0,
                })}
                width={({ numCols }) => ({
                  _: 12 / numCols,
                  m: 10 / numCols,
                  l: 7 / numCols,
                  xl: 8 / numCols,
                })}
              >
                {currentUser ? (
                  topic.closed ? null : (
                    <MemberRequestCard />
                  )
                ) : (
                  <Box display="block">
                    <Login restrictedAccess={topic.restricted_access} />
                  </Box>
                )}
              </Col>
              <Col
                offset={({ numCols }) => ({
                  _: 0,
                  m: 1 / numCols,
                  l: 0,
                  xl: 0,
                })}
                width={({ numCols }) => ({
                  _: 12,
                  m: 10 / numCols,
                  l: 5 / numCols,
                  xl: 4 / numCols,
                })}
              >
                <TopicSideBar topic={topic} {...otherProps} />
              </Col>
            </Row>
          </Grid>
        </Container>
      </>
    );
  }

  return (
    <>
      <DocumentTitle
        title={generatePageTitle({
          title: topicsMenuItem,
          networkName: getPageTitleFromNetwork(currentNetwork),
          topicName: topic?.name,
        })}
      />
      <LiveFeedApp
        {...otherProps}
        config={getFeedConfig(GROUP_FEED_TYPE, {
          currentUser,
          topic,
          showTypeHeader: true,
          sidebar: <TopicSideBar topic={topic} {...otherProps} />,
        })}
        amplitudeConfig={liveFeedAmplitudeConfig}
      />
    </>
  );
};

export default TopicLiveFeedContainer;
